import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import liff from "@line/liff";
import { LiffMockPlugin } from "@line/liff-mock";
import "./App.css";
import RegistrationForm from "./pages/RegistrationForm";
import CouponList from "./pages/CouponList";
import StampCard from "./pages/StampCard";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import ApprovalOutlinedIcon from "@mui/icons-material/ApprovalOutlined";
import PersonIcon from "@mui/icons-material/Person";
import { BottomNav, BottomNavItem } from "./components/styles";
import MyPage from "./pages/MyPage";
import StampCardList from "./pages/StampCardList";
import EditProfileForm from "./pages/EditProfileForm";
import GetCouponList from "./pages/GetCouponList";
import axios from "./service/AxiosBaseService";
import useUserStore from "./store/userStore";
import { User } from "./components/types";
import useAuthStore from "./store/authStore";

function App() {
  const location = useLocation();
  const [value, setValue] = React.useState(0);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugLog, setDebugLog] = useState<string[]>([]);
  const { isAuthenticated, setIsAuthenticated } = useAuthStore();
  const { setUser } = useUserStore();

  const logStage = (msg: string) => {
    const line = `${new Date().toISOString().slice(11, 19)} ${msg}`;
    try {
      const prev = JSON.parse(localStorage.getItem("liff_debug_log") || "[]");
      const next = [...prev, line].slice(-30);
      localStorage.setItem("liff_debug_log", JSON.stringify(next));
      setDebugLog(next);
    } catch {
      setDebugLog((prev) => [...prev, line]);
    }
  };

  useEffect(() => {
    try {
      const prev = JSON.parse(localStorage.getItem("liff_debug_log") || "[]");
      setDebugLog(prev);
    } catch {
      // ignore
    }

    const liffId = process.env.REACT_APP_LIFF_ID;

    if (!liffId) {
      console.error("LIFF_ID is not defined in environment variables");
      logStage("ERROR: LIFF_IDが環境変数に設定されていません");
      return;
    }

    const liffConfig = {
      liffId: liffId,
    };

    logStage("liff.init 開始");
    liff
      .init(liffConfig)
      .then(() => {
        logStage("liff.init 成功");
        if (!liff.isLoggedIn()) {
          logStage("未ログイン -> liff.login() 実行");
          return liff.login();
        }
        logStage("ログイン済み");
      })
      .then(() => {
        logStage("アクセストークン取得試行");
        const accessToken = liff.getAccessToken();
        if (accessToken) {
          logStage("アクセストークン取得成功 -> /users/login 呼び出し");
          axios
            .post("/users/login", { accessToken }, { withCredentials: true })
            .then((response) => {
              logStage(`/users/login 成功 (exists=${response.data.exists})`);
              if (response.data.exists) {
                setUser(response.data.user);
                setIsAuthenticated(true);
                setLoading(false);
                navigate("/mypage", { replace: true });
              } else {
                setLoading(false);
                navigate("/");
              }
            })
            .catch((error) => {
              setLoading(false);
              logStage(`ERROR: /users/login 失敗 ${error?.message ?? error}`);
              console.error("Login Error:", error);
              navigate("/");
            });
        } else {
          logStage("ERROR: アクセストークンが取得できませんでした");
          console.warn("Access token not found.");
          setLoading(false);
          navigate("/");
        }
      })
      .catch((err) => {
        logStage(`ERROR: liff.init/login 失敗 ${err?.message ?? err}`);
        console.error("LIFF initialization error:", err);
        setError(err?.message ?? String(err));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (location.pathname === "/mypage") {
      setValue(0);
    } else if (location.pathname === "/coupon") {
      setValue(1);
    } else if (location.pathname === "/stamp") {
      setValue(2);
    }
  }, [location.pathname]);

  return (
    <div className="App">
      {loading ? (
        <div className="loading-container">
          <p>Loading...</p>
          <pre style={{ fontSize: "10px", textAlign: "left", whiteSpace: "pre-wrap", padding: "8px" }}>
            {debugLog.join("\n")}
          </pre>
        </div>
      ) : error ? (
        <div className="loading-container">
          <p>エラーが発生しました: {error}</p>
          <pre style={{ fontSize: "10px", textAlign: "left", whiteSpace: "pre-wrap", padding: "8px" }}>
            {debugLog.join("\n")}
          </pre>
        </div>
      ) : (
        <>
          <Routes>
            {isAuthenticated ? (
              <>
                <Route path="/" element={<MyPage />} />
                <Route path="/mypage" element={<MyPage />} />
                <Route path="/mypage/edit" element={<EditProfileForm />} />
                <Route path="/mypage/coupon" element={<GetCouponList />} />
                <Route path="/coupon" element={<CouponList />} />
                <Route path="/stamp" element={<StampCard />} />
                <Route path="/stamp/index" element={<StampCardList />} />
              </>
            ) : (
              <Route path="/" element={<RegistrationForm />} />
            )}
          </Routes>
          {isAuthenticated ? (
            <BottomNav
              showLabels
              value={value}
              onChange={(event, newValue) => {
                setValue(newValue);
              }}
            >
              <BottomNavItem
                label="マイページ"
                onClick={() => {
                  setValue(0);
                  navigate("/mypage", { replace: true });
                }}
                icon={<PersonIcon />}
              />
              <BottomNavItem
                label="クーポン"
                onClick={() => {
                  setValue(1);
                  navigate("/coupon", { replace: true });
                }}
                icon={<ConfirmationNumberOutlinedIcon />}
              />
              <BottomNavItem
                label="スタンプ"
                onClick={() => {
                  setValue(2);
                  navigate("/stamp", { replace: true });
                }}
                icon={<ApprovalOutlinedIcon />}
              />
            </BottomNav>
          ) : (
            <></>
          )}
        </>
      )}
    </div>
  );
}

export default App;
